import { GridApplication } from 'src/app/+grid-application-shared/entities/gridApplication';

export const LookupKeys = {
  ConsumptionDeviceType: 'sk_consumption_device_type',
  RegistrationDeviceType: 'sk_registration_device_type',
  HeatingPumpType: 'sk_heating_pump_type',
  ModuleChoice: 'sk_module_choice',
  AirConditioningType: 'sk_air_conditioning_type',
  WallBoxType: 'sk_wall_box_type',
  LoadManagementType: 'sk_load_management_type',
  CouplingType: 'sk_coupling_type',
  SingleDeviceType: 'sk_single_device_type',
  MainsInterferenceDeviceType: 'sk_mains_interference_device',
  ConnectionType: 'sk_connection_type',
  StartUpType: 'sk_startup_type',
  StartUpCircuit: 'sk_startup_circuit',
  PowerConverterType: 'sk_power_converter_type',
  CenterPointCircuitType: 'sk_center_point_circuit_type',
};

export class DrrCcdDeviceApplicationDto extends GridApplication {
  is_registration_device?: boolean;
  is_consumption_device?: boolean;
  network_connection_point_power?: number;
  drr_ccd_devices?: DrrCcdDeviceDto[];
  is_commissioning_done?: boolean;
  is_commissioning_confirmed?: boolean;
  commissioning_date?: string;
  commissioning_document_id?: number;
  commissioning_document?: SimpleDocumentDto;
}

export interface GenDocumentDto {
  document_id: number;
  application_id?: number;
  offer_id?: number;
  order_id?: number;
  invoice_id?: number;
  sk_document_type?: string;
  name?: string;
  filename?: string;
  content?: string;
  content_type?: string;
  template?: string;
  example?: number;
  filepath?: string;
  photovoltaic_id?: number;
  inverter_id?: number;
  generator_id?: number;
  converter_id?: number;
  power_plant_protection_id?: number;
  integrated_protection_id?: number;
}

export interface SimpleDocumentDto {
  document_id: number;
  name?: string;
  document_type?: string;
  content?: string;
  content_type?: string;
}

export interface DeviceUnitDto {
  id: number;
  manufacturer?: string;
  designation_type?: string;
  power?: number;
  data_sheet_document?: SimpleDocumentDto;
  heater_power?: number;
  has_additional_heating?: boolean;
  data_sheet_wr_document_id?: number;
  data_sheet_wr_document?: SimpleDocumentDto;
  coupling_type?: string;
  has_load_management?: boolean;
  load_management_type?: string;
  parent_device_id?: number;
  parent_device?: DeviceUnitDto;
  children_devices?: DeviceUnitDto[];
  drr_ccd_device_id: number;
  unavailable: boolean;
  circuit_diagram_document_id?: number;
  circuit_diagram_document?: SimpleDocumentDto;
  storage_not_in_parallel_mode?: boolean;
  registration_unit_id?: number;
  registration_unit?: RegistrationUnitDto;
}

export interface RegistrationUnitDto {
  id: number;
  device_unit_id: number;
  device_name?: string;
  device_number?: string;
  heating_type?: string;
  sk_measuring_concept?: string;
  single_device_type?: string;
  data_collection_document_id?: number;
  data_collection_document?: SimpleDocumentDto;
  site_plan_installation_document_id?: number;
  site_plan_installation_document?: SimpleDocumentDto;
  is_system_installation_vde_arn4100?: boolean;
  is_power_provided_from_customer_system?: boolean;
  is_emergency_generator_not_in_parallel?: boolean;
  is_energy_from_emergency_generator_not_remunerated?: boolean;
  is_network_disturbance_complied_with_vde_arn4100?: boolean;
  is_input_of_customer_system_less_or_equal75?: boolean;
  mains_interference_device_type?: string;
  connection_type?: string;
  network_disturbances_data?: NetworkDisturbancesDataDto;
  network_disturbances_data_id?: number;
}

export interface NetworkDisturbancesDataDto {
  id: number;
  registration_unit_id: number;
  is_asynchronous_motor?: boolean;
  is_drive_with_power_converter?: boolean;
  rated_power?: number;
  rated_voltage?: number;
  rated_speed?: number;
  rated_current?: number;
  power_factor?: string;
  efficiency?: string;
  ratio_starting_current_rated?: string;
  start_up_type?: string;
  start_up_circuit?: string;
  other_startup_circuit?: string;
  load_number_rotation_changes?: string;
  attempts_per_hour?: number;
  attempts_per_day?: number;
  max_welding_performance?: number;
  weld_number?: number;
  weld_duration?: number;
  xray_rated_power?: number;
  xray_required_power?: number;
  power_converter_efficiency?: string;
  max_number_of_recordings?: number;
  commutation_inductance?: string;
  converter_transformer?: string;
  rectifier_design?: string;
  pulse_number?: string;
  switching_group?: string;
  power_converter_type?: string;
  relative_voltage?: number;
  center_point_circuit_type?: string;
  max_inrush_current?: number;
  reactive_power_range?: number;
  reactive_power_per_stage?: number;
  number_of_steps?: number;
  resonance_frequency?: string;
}

export class DrrCcdDeviceDto {
  id: number;
  device_type?: string;
  pump_type?: string;
  air_conditioning_type?: string;
  wall_box_type?: string;
  total_grid_power_consumption?: number;
  type_of_control?: string;
  module_choice?: string;
  is_cascade_measurement?: boolean;
  is_separate_meter_required?: boolean;
  meter_number?: string;
  meter2?: string;
  device_units?: DeviceUnitDto[];
  drr_ccd_device_application_id: number;
  data_sheet_document: GenDocumentDto;
  data_sheet_wr_document: GenDocumentDto;
  other_type?: string;
}

//#region FORM MODELS
export interface CcdDrrDevicesFormModel {
  applicationId: number;
  type: string;
  devices: DeviceWrapperFormModel[];
  simultaneouslyRequiredPower: number;
  commissioningDate?: Date | null;
  isCommissioningDone?: boolean | null;
  isCommissioningConfirmed?: boolean | null;
  commissioningDocument?: FileFormModel;
  commissioningDocumentId?: number;
}

export interface FileFormModel {
  id?: number;
  name: string;
  documentType?: string;
  file: any; // Consider specifying a more precise type instead of 'any'
  contentType: string | null;
}

export interface DeviceUnitFormModel {
  id: number;
  drrCcdDeviceId: number;
  manufacturer: string;
  designationType: string;
  electricalPower: number;
  powerOfAdditionalHeater: number;
  hasAdditionalHeating: boolean;
  unavailable: boolean;
  loadManagementAvailable: boolean | null;
  loadManagementType: string | null;
  couplingType: string;
  subUnits?: DeviceUnitFormModel[];
  dataSheet?: FileFormModel;
  dataSheetWr?: FileFormModel;
  circuitDiagramDocument?: FileFormModel;
  storageNotInParallelMode?: boolean;
  registrationUnit?: RegistrationUnitFormModel;
}

export interface RegistrationUnitFormModel {
  id: number;
  deviceUnitId: number;
  deviceName: string;
  deviceNumber: string;
  heatingType: string;
  skMeasuringConcept: string;
  singleDeviceType: string;
  dataCollectionDocument?: FileFormModel;
  sitePlanInstallationDocument?: FileFormModel;
  isSystemInstallationVdeArn4100?: boolean;
  isPowerProvidedFromCustomerSystem?: boolean;
  isEmergencyGeneratorNotInParallel?: boolean;
  isEnergyFromEmergencyGeneratorNotRemunerated?: boolean;
  isNetworkDisturbanceCompliedWithVdeArn4100?: boolean;
  isInputOfCustomerSystemLessOrEqual75?: boolean;
  mainsInterferenceDeviceType?: string;
  connectionType?: string;
  networkDisturbancesData?: NetworkDisturbancesDataFormModel;
}

export interface NetworkDisturbancesDataFormModel {
  id: number;
  registrationUnitId: number;
  isAsynchronousMotor?: boolean;
  isDriveWithPowerConverter?: boolean;
  ratedPower?: number;
  ratedVoltage?: number;
  ratedSpeed?: number;
  ratedCurrent?: number;
  powerFactor?: string;
  efficiency?: string;
  ratioStartingCurrentRated?: string;
  startUpType?: string;
  startUpCircuit?: string;
  otherStartupCircuit?: string;
  loadNumberRotationChanges?: string;
  attemptsPerHour?: number;
  attemptsPerDay?: number;
  maxWeldingPerformance?: number;
  weldNumber?: number;
  weldDuration?: number;
  xrayRatedPower?: number;
  xrayRequiredPower?: number;
  powerConverterEfficiency?: string;
  maxNumberOfRecordings?: number;
  commutationInductance?: string;
  converterTransformer?: string;
  rectifierDesign?: string;
  pulseNumber?: string;
  switchingGroup?: string;
  powerConverterType?: string;
  relativeVoltage?: number;
  centerPointCircuitType?: string;
  maxInrushCurrent?: number;
  reactivePowerRange?: number;
  reactivePowerPerStage?: number;
  numberOfSteps?: number;
  resonanceFrequency?: string;
}

export interface DeviceUnitsFormModel {
  units: DeviceUnitFormModel[];
}

export interface DeviceFormModel {
  id?: number;
  rawValue?: DeviceFormModel;
  ccdType: string;
  heatingPumpType: string;
  airConditioningType: string | null;
  wallBoxType: string | null;
  other: string | null;
  totalConsumption: number;
  typeOfControl: string;
  module: string;
  cascadeMeasurementDesired: boolean;
  separateMeterRequired: boolean;
  meterNumber: string | null;
  meter2: string | null;
  deviceUnits: DeviceUnitsFormModel;
}

export interface DeviceWrapperFormModel {
  device: DeviceFormModel;
}
//#endregion

export const DrrCcdTypes = {
  heatPump: 'heat_pump',
  airConditioner: 'air_conditioner',
  wallBox: 'wall_box',
  storage: 'storage',
  chargingDevice: 'charging_device',
  heating: 'heating',
  singleDevice: 'single_device',
  energyManagement: 'energy_management',
  emergencyGenerator: 'emergency_generator',
  mainsInterference: 'mains_interference',
  other: 'other',
};

export interface DrrCcdCommissioningData {
  commissioning_date?: Date;
  is_commissioning_done?: boolean;
  is_commissioning_confirmed?: boolean;
  commissioning_document_id?: number;
  commissioning_document?: FileFormModel;
}

export enum DrrCcdDocumentTypes {
  DataSheet = 'drr_ccd_data_sheet',
  DataSheetWR = 'drr_ccd_data_sheet_wr',
  CommissioningDocument = 'drr_ccd_commissioning',
  CircuitDiagramDocument = 'drr_ccd_circuit_diagram',
  DataCollectionDocument = 'drr_ccd_data_collection',
  SitePlanInstallationDocument = 'drr_ccd_site_plan_installation',
}

export const MainsInterferenceDeviceTypes = {
  motors: 'motors',
  weldingMachines: 'welding_machines',
  xrayMachines: 'xray_machines',
  applianceWithConverters: 'appliance_with_converters',
  transformers: 'transformers',
  reactivePowerCompensationSystems: 'reactive_power_compensation_systems',
};
